import React, { useContext } from 'react';
import { ContactContext } from '../../../../../context/ContactContext';
import { CustomFieldEntityType } from '../../../../../types/custom_field/customField';
import { CustomFieldContext } from '../../../../../context/CustomFieldContext';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import { UserContext } from '../../../../../context/UserContext';
import { OrderInput, OrderUtil } from '../../../../../types/order/';
import { BusinessCentral } from '../../../../../types/common/integration/bc';
import { SAP } from '../../../../../types/common/integration/sap';
import { t } from '../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { TextInput } from '../../../../components/TextInput';
import CustomFieldOrderInput from '../../../Common/CustomFieldOrderInput';
import { AddressUtil } from '../../../../../types/common/address.util';
import { DateTimeUtil } from '../../../../../util/date.util';
import { FlowType } from '../../../../../types/order/orderFlow';
import { OrderFlowContext } from '../../../../../context/OrderFlowContext';
import { OrderContext } from '../../../../../context/OrderContext';

interface OrderInfoPaneProps {
  order?: OrderInput;
  setOrder: (order: OrderInput) => void;
  footer: (canUpdate?: boolean) => JSX.Element;
}

export default function OrderInfoPane({ order, setOrder, footer }: OrderInfoPaneProps) {
  if (!order) return null;

  const { currentCompany } = useContext(CompanyContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { contacts } = useContext(ContactContext);
  const { customFields } = useContext(CustomFieldContext);
  const { companyUsers, locale } = useContext(UserContext);
  const { orderFlows } = useContext(OrderFlowContext);
  const { orders } = useContext(OrderContext);

  const entitySubtype = OrderUtil.typeToEntitySubType(order.type);

  const customFieldItems = [...customFields.values()]
    .filter(
      v => v.entityType === CustomFieldEntityType.order && (!v.entitySubtype || v.entitySubtype === entitySubtype),
    )
    .sort((a, b) => a.index - b.index || a.name.localeCompare(b.name));
  const inputValues = order.customFieldValues();
  const defaultValues = customFieldItems.map(cf => cf.toValue());
  const customFieldValues = inputValues.length ? [...inputValues] : defaultValues;

  const fieldsDisabled = true;
  // TODO_ORDER
  // const fieldsDisabled =
  //   order.status === OrderStatus.complete ||
  //   order.status === OrderStatus.cancelled ||
  //   order.status === OrderStatus.failed ||
  //   order.status === OrderStatus.disabled;

  const disabled =
    fieldsDisabled ||
    !!customFieldItems
      .filter(cf => cf.mandatory)
      .filter(cf => {
        if (!order.customFields.has(cf.id)) return true;
        if (!order.customFields.get(cf.id)!.value) return true;
        return false;
      }).length ||
    BusinessCentral.isPurchaseOrder(order) ||
    SAP.isInboundDelivery(order) ||
    SAP.isOutboundDelivery(order);

  return (
    <div className='flex h-full flex-col'>
      <div className='flex-grow overflow-y-auto'>
        <div className='grid gap-2'>
          <div className='grid grid-cols-2 gap-2'>
            <TextInput disabled label={t(TranslationKey.order).singular.label} value={order.number || '0'} />
            <TextInput
              disabled
              label={t(TranslationKey.orderType).singular.label}
              value={orderFlows.get(order.flowId)?.name || ''}
            />
          </div>
          <div className='grid grid-cols-2 gap-2'>
            {order.fromStockLocationId && (
              <TextInput
                disabled
                label={
                  order.type === FlowType.inbound
                    ? t(TranslationKey.destinationStockLocation).singular.label
                    : t(TranslationKey.sourceStockLocation).singular.label
                }
                value={stockLocations.get(OrderUtil.determineStockLocation(order))?.name || ''}
              />
            )}
            {order.toStockLocationId && (
              <TextInput
                disabled
                label={
                  order.type === FlowType.inbound
                    ? t(TranslationKey.destinationStockLocation).singular.label
                    : t(TranslationKey.sourceStockLocation).singular.label
                }
                value={stockLocations.get(OrderUtil.determineStockLocation(order))?.name || ''}
              />
            )}
          </div>
          <div className='grid grid-cols-2 gap-2'>
            <TextInput
              disabled={true}
              value={DateTimeUtil.toLocaleDateTimeString(locale, new Date(order.createdAt))}
              label={t(TranslationKey.createdAt).singular.label}
              onChange={v => {}}
              placeholder={t(TranslationKey.createdAt).singular.label}
            />
            <TextInput
              disabled={true}
              value={`${companyUsers.get(order.createdBy)?.email || 'Unknown user'}`}
              label={t(TranslationKey.createdBy).singular.label}
              onChange={v => {}}
              placeholder={t(TranslationKey.createdBy).singular.label}
            />
          </div>
          {order.contactId && (
            <div className='grid grid-cols-2 gap-2'>
              <div className='col-span-1'>
                <TextInput
                  label={t(TranslationKey.contact).singular.label}
                  disabled={true}
                  value={contacts.get(order.contactId)?.name}
                />
              </div>
              {order.address && (
                <div className='col-span-1'>
                  <TextInput
                    label={t(TranslationKey.address).singular.label}
                    disabled={true}
                    value={AddressUtil.addressToSearchQuery(order.address)}
                  />
                </div>
              )}
            </div>
          )}
          {order.parentId && (
            <div className='grid grid-cols-2 gap-2'>
              <div className='col-span-1'>
                <TextInput
                  label={t(TranslationKey.completedReturn).singular.label}
                  disabled={true}
                  value={orders.get(order.parentId)?.number}
                />
              </div>
              {order.address && (
                <div className='col-span-1'>
                  <TextInput
                    label={t(TranslationKey.address).singular.label}
                    disabled={true}
                    value={AddressUtil.addressToSearchQuery(order.address)}
                  />
                </div>
              )}
            </div>
          )}
          {customFieldValues.length ? (
            <div>
              <CustomFieldOrderInput key={'cfv'} disabled={disabled} order={order} setOrder={setOrder} />
            </div>
          ) : null}
        </div>
      </div>
      <div className='mt-2'>{footer()}</div>
    </div>
  );
}
